import * as React from 'react';
import { useContext, useState } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import UnifiedFrame, { useWorld } from '@/src/v3/chrome/UnifiedFrame';
import { WEB_V3_FLAG } from '@/src/v3/flags';
import FeatureFlagContext from '@/src/Features/components/FeatureFlagContext';
import TopBar from './TopBar';
import Dock, { DockItem } from './Dock';
import SwitcherMenu from './SwitcherMenu';
import SearchField from './SearchField';
import JumpMenu from './JumpMenu';
import { Switch } from '@/src/shadComponents/ui/switch';
import { Toaster } from 'sonner';
import useAuth from '@/src/Authentication/components/hooks/useAuth';
import useUserOrg from '@/src/Authentication/components/hooks/useUserOrg';
import OrgContext from '@/src/Authentication/components/OrgContext';

/*
 * V2Shell — the shared chrome shell: .psv2 scope, persisted dark mode,
 * TopBar (org circle · context switcher · account avatar) and the Dock.
 * Chromes supply the context switcher payload; the org and account
 * menus are shell-owned so they exist at every level.
 */

const DARK_KEY = 'psv2-dark';


interface V2ShellProps {
    back?: DockItem;
    items: DockItem[];
    actions?: DockItem[];
    /* false = the chrome renders the router Outlet itself (inside its
       own layout, e.g. V2OrgChrome's settings grid) — without this the
       page would mount twice */
    outlet?: boolean;
    children?: React.ReactNode;
}

/*
 * OrgRow — a quiet single row showing the current org; the full
 * (searchable) list only appears on tap. Lives inside the account
 * popover, which unmounts on close, so the expansion resets itself.
 */
function OrgRow({
    orgs,
    current,
    onPick,
}: {
    orgs: any[];
    current: any;
    onPick: (org: any) => void;
}) {
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState('');
    const list =
        query.trim().length > 0
            ? orgs.filter((o: any) =>
                  o.name
                      .toLowerCase()
                      .includes(query.trim().toLowerCase()),
              )
            : orgs;
    return (
        <div>
            <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                className="flex w-full cursor-pointer items-center gap-2 rounded-[8px] border-0 bg-transparent px-2.5 py-2 text-left font-[inherit] text-[13px] text-muted-foreground hover:bg-secondary"
            >
                <span className="flex-1">Organization</span>
                <span className="max-w-[120px] truncate font-semibold text-foreground">
                    {current?.name ?? '—'}
                </span>
                <span aria-hidden className="text-[10px] text-faint">
                    ▾
                </span>
            </button>
            {open && (
                <div className="mb-1">
                    {orgs.length > 8 && (
                        <div className="px-1 pb-1.5 pt-0.5">
                            <SearchField
                                autoFocus
                                value={query}
                                onChange={setQuery}
                                placeholder="Search…"
                                className="w-full"
                            />
                        </div>
                    )}
                    <div className="max-h-[240px] overflow-y-auto">
                        {list.map((o: any) => (
                            <button
                                key={o.id}
                                type="button"
                                onClick={() => {
                                    if (o.id !== current?.id) onPick(o);
                                }}
                                className={
                                    'flex w-full cursor-pointer items-center gap-2 rounded-[8px] border-0 bg-transparent px-2.5 py-2 text-left font-[inherit] text-[13px] hover:bg-secondary ' +
                                    (o.id === current?.id
                                        ? 'font-semibold'
                                        : 'text-muted-foreground')
                                }
                            >
                                <span
                                    className={
                                        'h-1.5 w-1.5 flex-none rounded-full ' +
                                        (o.id === current?.id
                                            ? 'bg-brand'
                                            : 'bg-transparent')
                                    }
                                />
                                <span className="min-w-0 flex-1 truncate">
                                    {o.name}
                                </span>
                            </button>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}

export default function V2Shell({
    back,
    items,
    actions,
    outlet = true,
    children,
}: V2ShellProps) {
    const [dark, setDark] = useState(
        () => localStorage.getItem(DARK_KEY) === '1',
    );
    const navigate = useNavigate();
    const { auth, signout } = useAuth() as any;
    const { org } = useUserOrg();
    const { setOrg } = useContext(OrgContext) as any;
    const orgs: any[] = auth?.orgs ?? [];

    /* in the v3 world, v2 pages render inside the v3 frame instead of the top bar + dock */
    const flags = useContext(FeatureFlagContext) as Record<string, boolean>;
    const unified = useWorld(!!flags?.[WEB_V3_FLAG]);

    const toggleDark = (value: boolean) => {
        setDark(value);
        localStorage.setItem(DARK_KEY, value ? '1' : '0');
    };

    const initials = (value?: string) =>
        (value ?? '?')
            .split(/[\s@._-]+/)
            .filter(Boolean)
            .slice(0, 2)
            .map((part: string) => part[0])
            .join('')
            .toUpperCase();

    if (unified) {
        return (
            <UnifiedFrame>
                <div className="psv2">
                    {outlet && <Outlet />}
                    {children}
                </div>
            </UnifiedFrame>
        );
    }

    return (
        /* overflow-x-clip: full-bleed elements (hero chart) may exceed
           the viewport by the scrollbar width — clip, don't scroll */
        <div
            className={
                'psv2 min-h-screen overflow-x-clip pb-32' +
                (dark ? ' dark' : '')
            }
        >
            <TopBar
                left={
                    <>
                        {/* org mark: always one tap back to the Dashboard */}
                        <button
                            type="button"
                            title="Dashboard"
                            onClick={() => navigate('/v2')}
                            className="flex cursor-pointer border-0 bg-transparent p-0"
                        >
                            <span className="flex h-[36px] w-[36px] items-center justify-center rounded-full bg-foreground font-mono text-[13px] font-semibold text-background">
                                {(org?.name ?? 'P')[0].toUpperCase()}
                            </span>
                        </button>
                    </>
                }
                right={
                    <>
                    {/* quick-nav search — every page, beside the avatar */}
                    <JumpMenu />
                    <SwitcherMenu
                        triggerTitle="Account"
                        align="right"
                        width={260}
                        trigger={
                            <span className="flex h-[36px] w-[36px] items-center justify-center rounded-full bg-secondary font-mono text-[11.5px] font-semibold text-muted-foreground">
                                {initials(auth?.username)}
                            </span>
                        }
                        top={
                            <div className="mb-1 flex items-center gap-2.5 border-b border-line px-2.5 pb-2.5 pt-1.5">
                                <span className="flex h-[31px] w-[31px] flex-none items-center justify-center rounded-full bg-secondary text-[11px] font-semibold text-muted-foreground">
                                    {initials(auth?.username)}
                                </span>
                                <span className="min-w-0 flex-1 truncate text-[13px] font-semibold">
                                    {auth?.username}
                                </span>
                            </div>
                        }
                        items={[]}
                        footer={[
                            /* org settings — Admin/GlobalAdmin only (the
                               /v2/org route guard enforces the same set) */
                            ...(auth?.roles?.some((role: number) =>
                                [0, 69].includes(role),
                            )
                                ? [
                                      {
                                          key: 'org',
                                          label: 'Organization',
                                          onClick: () =>
                                              navigate('/v2/org'),
                                      },
                                  ]
                                : []),
                            /* platform admin — GlobalAdmin only */
                            ...(auth?.roles?.includes(69)
                                ? [
                                      {
                                          key: 'admin',
                                          label: 'Platform admin',
                                          onClick: () =>
                                              navigate('/v2/admin'),
                                      },
                                  ]
                                : []),
                            {
                                key: 'devices',
                                label: 'Devices',
                                onClick: () => navigate('/v2/devices'),
                            },
                            {
                                key: 'classic',
                                label: 'Classic app ↗',
                                onClick: () => navigate('/home'),
                            },
                            {
                                key: 'logout',
                                label: 'Log out',
                                onClick: async () => {
                                    await signout?.().catch(() => null);
                                    navigate('/login');
                                },
                            },
                        ]}
                    >
                        <OrgRow
                            orgs={orgs}
                            current={org}
                            onPick={(o: any) => {
                                setOrg(o);
                                /* clean reload — every page's data is
                                   org-scoped */
                                window.location.assign('/v2');
                            }}
                        />
                        <label className="flex cursor-pointer items-center justify-between gap-2 rounded-[8px] px-2.5 py-2 text-[13px] text-muted-foreground hover:bg-secondary">
                            Dark mode
                            <Switch
                                checked={dark}
                                onCheckedChange={toggleDark}
                            />
                        </label>
                    </SwitcherMenu>
                    </>
                }
            />
            {outlet && <Outlet />}
            <Dock back={back} items={items} actions={actions} />
            {children}
            {/* on-brand toasts — house card grammar, follows the
                shell's own dark toggle (the shadcn wrapper rode
                next-themes + classic styling) */}
            <Toaster
                position="bottom-left"
                gap={8}
                toastOptions={{
                    unstyled: true,
                    classNames: {
                        toast: 'psv2 pointer-events-auto flex w-[300px] items-center gap-2.5 rounded-[14px] border border-border bg-background px-4 py-3 font-[inherit] text-[12.5px] font-semibold text-foreground shadow-[0_12px_40px_rgba(0,0,0,0.14)]' + (dark ? ' dark' : ''),
                        title: 'font-semibold',
                        description:
                            'text-[12px] font-normal text-muted-foreground',
                        icon: 'flex h-4 w-4 flex-none items-center justify-center [&>svg]:h-full [&>svg]:w-full',
                        success: '[&_svg]:text-good',
                        error: '[&_svg]:text-attention',
                    },
                }}
            />
        </div>
    );
}
